import React, { useEffect, useState, useCallback } from 'react';
import Chart from 'chart.js';
import { toast } from 'react-toastify';
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Form,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Button
} from 'reactstrap';
import { chartOptions, parseOptions } from 'variables/charts.js';
import ReactLoader from 'components/Loader';
import { useSelector } from 'react-redux';

import { useForm } from 'react-hook-form';
import CRMDashboardService from '../../service';
import randomColor from 'randomcolor';
import GraphicLeadsByResponsible from './GraphicLeadsByResponsible';
import GraphicLeadsByStage from './GraphicLeadsByStage';
import { entitiesName } from 'views/CRM/shared/utils/entitiesName';
import { useRecordTypesByUser } from 'views/RecordType/hooks/useRecordTypesByUser';
import { buttonStyle } from 'layouts/LoginAutoAuth/components/utils';
import { toISOFormat, firstDayOfMonth } from '../../shared/dataFormater';
import GraphicLeadsByListCustomFields from './GraphicLeadsByListCustomFields';
import GraphicLeadsByResponsibleAndStatus from './GraphicLeadsByResponsibleAndStatus.js';
import DefaultDashboardFieldsFilterForm from './form/DefaultFieldsFilterForm';
import styled from 'styled-components';
import { FiChevronDown, FiChevronRight } from 'react-icons/fi';
import CustomFieldsFilterForm from 'views/CRM/shared/components/CustomFieldsFilterForm';
import { useEntityCustomFieldsByRecordType } from 'views/fieldConfigs/hooks/useCustomFieldsByRecordType';
import { customFieldTypes } from 'views/fieldConfigs/utils/customFieldTypes';
import { parseIntFromString } from 'utils/fuctions/parseIntFromString';

if (window.Chart) {
  parseOptions(Chart, chartOptions());
}
const filterLeadsCountByListCustomField = (data) => {
  if (!data) return;
  const result = data.filter((item) => {
    const allQuantitiesZero = item.options.every(
      (option) => option.quantity === 0
    );

    return !allQuantitiesZero;
  });
  return result;
};

const CustomFieldsFilterToggle = styled.div`
  display: inline-block;
  margin-bottom: 1rem;
  user-select: none;
  :hover {
    cursor: pointer;
    text-decoration: underline;
  }
`;

const CRMDashboard = () => {
  const [loading, setLoading] = useState(false);
  const [leadsCountByStage, setLeadsCountByStage] = useState(null);
  const [leadsCountByResponsible, setLeadsCountByResponsible] = useState(null);
  const [leadsCountByListCustomFields, setLeadsCountByListCustomFields] =
    useState(null);
  const [
    leadsCountByResponsibleAndStatus,
    setLeadsCountByResponsibleAndStatus
  ] = useState(null);
  const [defaultRecordType, setDefaultRecordType] = useState(null);
  const { control, handleSubmit, setValue, getValues, register } = useForm();
  const {
    recordTypes: leadRecordTypes,
    getRecordTypesByUser: getLeadRecordTypesByUser
  } = useRecordTypesByUser(entitiesName.LEAD);

  const { customFields, getCustomFields } = useEntityCustomFieldsByRecordType(
    entitiesName.LEAD,
    defaultRecordType?.value || defaultRecordType?.id
  );

  const [showCustomFieldsFilters, setShowCustomFieldsFilters] = useState(false);
  const systemConfiguration = useSelector((state) => state.systemConfiguration);

  useEffect(() => {
    getLeadRecordTypesByUser();
  }, [getLeadRecordTypesByUser]);

  useEffect(() => {
    if (leadRecordTypes !== null) {
      const firstRecordType = leadRecordTypes[0] || [];
      setDefaultRecordType({
        label: firstRecordType.name,
        value: firstRecordType.id
      });
    }
  }, [leadRecordTypes]);

  useEffect(() => {
    getCustomFields();
  }, [getCustomFields]);

  useEffect(() => {
    setLoading(true);

    if (defaultRecordType) {
      const formatedParams = {
        dateBegin: `${toISOFormat(firstDayOfMonth)}`,
        dateEnd: `${toISOFormat(new Date().toLocaleDateString('pt-br'))}`,
        recordType: defaultRecordType.value || defaultRecordType.id
      };
      getDashboardLeads(formatedParams);
    }
  }, [defaultRecordType]);

  const getDashboardLeads = (params) => {
    CRMDashboardService.getLeadsCountByStageWithDate(params)
      .then((response) => {
        const listCustomFields = response?.data?.data;
        setLeadsCountByStage(listCustomFields.leadsCountByStage);
        setLeadsCountByResponsible(listCustomFields.leadsCountByResponsible);
        setLeadsCountByResponsibleAndStatus(
          listCustomFields.leadsCountByStageStatus
        );
        const filteredResults = filterLeadsCountByListCustomField(
          listCustomFields.leadsCountByListCustomField
        );
        setLeadsCountByListCustomFields(filteredResults);
      })
      .catch((error) => {
        toast.error(error || 'Ops, ocorreu um erro!', {
          autoClose: 3000,
          closeOnClick: true
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const getGraphicLabels = () => {
    if (!leadsCountByStage) return [];
    return leadsCountByStage?.map((item) => item.name);
  };

  const getGraphicData = () => {
    if (!leadsCountByStage) return [];
    return leadsCountByStage?.map((item) => item.quantity);
  };

  const getGraphicColors = () => {
    if (!leadsCountByStage) return [];
    return leadsCountByStage?.map(() =>
      randomColor({ luminosity: 'random', hue: 'random' })
    );
  };

  const getGraphicTotalData = () => {
    if (!leadsCountByStage) return 0;
    return leadsCountByStage?.reduce((acc, item) => acc + item.quantity, 0);
  };

  const handleSelectRecordType = useCallback(
    (recordTypeId) => {
      setDefaultRecordType(
        leadRecordTypes.find((rt) => rt.id === recordTypeId)
      );
    },
    [leadRecordTypes]
  );

  const onSubmit = (data) => {
    try {
      let formatedData;

      formatedData = {
        ...data,
        dateBegin: `${toISOFormat(data.dateBegin)}`,
        dateEnd: `${toISOFormat(data.dateEnd)}`,
        filterDateBy: data.filterDateBy,
        recordType: data.selectedRecordType || ''
      };
      if (customFields && showCustomFieldsFilters) {
        customFields.forEach((field) => {
          if (field.type === customFieldTypes.LIST) {
            const possibleValues = field.options.map((value) => value.value);
            if (possibleValues.includes(data[field.name])) {
              formatedData[field.name] = data[field.name];
            }
          }
        });
      }

      const currencyFieldsName =
        customFields &&
        customFields
          .filter((field) => field.type === customFieldTypes.CURRENCY)
          .map((field) => field.name);

      formatedData.recordType = defaultRecordType.id || defaultRecordType.value;

      Object.keys(data).forEach((key) => {
        if (currencyFieldsName.includes(key)) {
          if (data[key]) {
            formatedData[key] = parseIntFromString(data[key]);
          }
        }
      });

      getDashboardLeads(formatedData);
    } catch (err) {
      toast.error(err || 'Ops, ocorreu um erro!', {
        autoClose: 3000,
        closeOnClick: true
      });
    }
  };

  if (loading) {
    return (
      <Row className="justify-content-md-center mt-4">
        <ReactLoader />
      </Row>
    );
  }

  const SelectRecordTypeDropDown = ({
    recordTypes = [],
    selectedRecordTypeName = '',
    handleSelectRecordType
  }) => {
    const [dropdownOpen, setDropdownOpen] = useState(false);

    const toggle = () => setDropdownOpen((prevState) => !prevState);

    return (
      <Dropdown isOpen={dropdownOpen} toggle={toggle}>
        <DropdownToggle
          tag="span"
          className="selected-record-type-name"
          data-toggle="dropdown"
          aria-expanded={dropdownOpen}
        >
          {selectedRecordTypeName}
        </DropdownToggle>
        <DropdownMenu>
          <DropdownItem header>Selecione</DropdownItem>
          {recordTypes.map((rt) => (
            <DropdownItem
              key={rt.id}
              onClick={() => handleSelectRecordType(rt.id)}
            >
              {rt.name}
            </DropdownItem>
          ))}
        </DropdownMenu>
      </Dropdown>
    );
  };

  return (
    <Container fluid className="pt-4">
      <Card>
        <CardBody>
          {leadRecordTypes ? (
            <div>
              <div className="small">
                <span className="font-weight-bold">Tipo de registro: </span>
                <SelectRecordTypeDropDown
                  recordTypes={leadRecordTypes}
                  selectedRecordTypeName={
                    defaultRecordType.label || defaultRecordType.name || ''
                  }
                  handleSelectRecordType={handleSelectRecordType}
                />
              </div>
              <Form
                className="needs-validation"
                onSubmit={handleSubmit(onSubmit)}
              >
                <DefaultDashboardFieldsFilterForm
                  setValue={setValue}
                  control={control}
                  getValues={getValues}
                  recordTypeId={defaultRecordType.value || defaultRecordType.id}
                  register={register}
                />

                {customFields && customFields.length > 0 && (
                  <>
                    <CustomFieldsFilterToggle
                      className="small"
                      onClick={() =>
                        setShowCustomFieldsFilters(!showCustomFieldsFilters)
                      }
                    >
                      Filtrar por campos customizados{' '}
                      {showCustomFieldsFilters ? (
                        <FiChevronDown size={18} />
                      ) : (
                        <FiChevronRight size={18} />
                      )}
                    </CustomFieldsFilterToggle>

                    {showCustomFieldsFilters && (
                      <CustomFieldsFilterForm
                        toISOFormat={toISOFormat}
                        getValues={getValues}
                        customFields={customFields}
                        setValue={setValue}
                        control={control}
                      />
                    )}
                  </>
                )}

                <br />

                <Button
                  type="submit"
                  style={buttonStyle(systemConfiguration.primary_color)}
                  className="mb-2"
                >
                  Buscar
                </Button>
              </Form>
              <Row>
                <Col>
                  <Card>
                    <CardHeader>
                      <h6 className="surtitle">Visão Geral</h6>
                      <h5 className="h3 mb-0">
                        Distribuição de Leads por Fase
                      </h5>
                    </CardHeader>
                    <CardBody>
                      <GraphicLeadsByStage
                        filteredLabels={getGraphicLabels()}
                        filteredData={getGraphicData()}
                        filteredColors={getGraphicColors()}
                        count={getGraphicTotalData()}
                      />
                    </CardBody>
                  </Card>

                  <Card>
                    <CardHeader>
                      <h6 className="surtitle">Visão Geral</h6>
                      <h5 className="h3 mb-0">
                        Distribuição de Leads por responsável
                      </h5>
                    </CardHeader>
                    <CardBody>
                      {leadsCountByResponsible &&
                        !!leadsCountByResponsible.length && (
                          <GraphicLeadsByResponsible
                            leadsCountByResponsible={leadsCountByResponsible}
                          />
                        )}
                    </CardBody>
                  </Card>

                  <Card>
                    <CardHeader>
                      <h6 className="surtitle">Visão Geral</h6>
                      <h5 className="h3 mb-0">
                        Distribuição de Leads por campos personalizados do tipo
                        Lista
                      </h5>
                    </CardHeader>
                    <CardBody
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(1, 1fr)',
                        justifyContent: 'center'
                      }}
                    >
                      {/* Dados dos gráficos */}
                      {leadsCountByListCustomFields &&
                        !!leadsCountByListCustomFields.length &&
                        leadsCountByListCustomFields.map((item) => (
                          <GraphicLeadsByListCustomFields
                            key={item.id}
                            leadsCountByListCustomFields={item}
                          />
                        ))}
                    </CardBody>
                  </Card>

                  <Card>
                    <CardHeader>
                      <h6 className="surtitle">Visão Geral</h6>
                      <h5 className="h3 mb-0">
                        Distribuição de Leads por responsável e status
                      </h5>
                    </CardHeader>
                    <CardBody
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(1, 1fr)',
                        justifyContent: 'center'
                      }}
                    >
                      {leadsCountByResponsibleAndStatus &&
                        !!leadsCountByResponsibleAndStatus.length && (
                          <GraphicLeadsByResponsibleAndStatus
                            leadsCountByResponsibleAndStatus={
                              leadsCountByResponsibleAndStatus
                            }
                          />
                        )}
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </div>
          ) : (
            <div>
              <span className="small">
                Você não tem acesso a nenhum tipo de lead.
              </span>
            </div>
          )}
        </CardBody>
      </Card>
    </Container>
  );
};

export default CRMDashboard;
